<template>
  <div class="ma-8">
    <v-overlay :value="loaderOverlay" :absolute="true">
      <v-row justify="center">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-row>
    </v-overlay>

    <v-card v-if="!loaderOverlay" :color="$vuetify.theme.dark ? '#2E2E2E' : 'grey lighten-3'" min-width="640" class="pb-10">
      <v-card-title>
        <v-row justify="start" no-gutters class="mr-5">
          <v-icon large class="pr-2">mdi-account-box-multiple-outline </v-icon>
          <h3>{{ $t("eventLogs") }}</h3>

          <v-spacer />
        </v-row>
      </v-card-title>

      <v-card class="mx-10 pa-2 my-2">
        <v-row class="pa-0 ma-0">
          <v-spacer />

          <v-row fill-height align="center" justify="end" class="pr-5">
            <v-btn prepend-icon="mdi-filter-outline" @click="handleSelectionClick" color="red white--text">
              {{ $t("filterColumns") }}
            </v-btn>
          </v-row>

          <v-divider vertical class="mx-3"></v-divider>

          <div class="d-flex">
            <v-select :label="$t('pageSize')" :items="pageSizes" v-model="pageSize" outlined hide-details dense></v-select>
          </div>

          <v-divider v-if="list.length > 0" vertical class="mx-3"></v-divider>

          <v-tooltip v-if="list.length > 0" bottom color="primary" class="white-text">
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon @click="handleCSVExport">
                <v-icon large> mdi-file-export-outline </v-icon>
              </v-btn>
            </template>
            <span>{{ $t("logfileExport") }}</span>
          </v-tooltip>
        </v-row>
      </v-card>

      <v-card class="mx-10 my-2">
        <v-row class="pa-0 ma-0 mx-2 align-center">
          <v-col>
            <v-text-field :label="$t('search')" v-model="searchTerm" clearable prepend-inner-icon="mdi-magnify" hide-details dense></v-text-field>
          </v-col>
          <v-col style="display: inline-flex" class="mx-2">
            <v-icon large>mdi-calendar-arrow-left</v-icon>

            <DatePicker v-model="date.from" dense :mydate1="date.from" :label="$t('dateFrom')" />
          </v-col>

          <v-col style="display: inline-flex" class="mx-2">
            <v-icon large>mdi-calendar-arrow-right</v-icon>

            <DatePicker v-model="date.until" dense :mydate1="date.until" :label="$t('dateUntil')" />
          </v-col>

          <v-tooltip bottom color="primary" class="white-text">
            <template v-slot:activator="{ on }">
              <div v-on="on">
                <v-btn v-if="!refetchActive" class="mx-3" color="primary" @click="refetch">{{ $t("refresh") }}</v-btn>
                <v-btn v-else class="mx-3" color="primary" icon>
                  <v-icon class="rotate">mdi-refresh</v-icon>
                </v-btn>
              </div>
            </template>
            <span>{{ lastRefresh }}</span>
          </v-tooltip>
        </v-row>
      </v-card>

      <v-card class="mx-10 my-5" v-if="list.length === 0">
        <v-row>
          <v-spacer />

          <v-col style="text-align: center; font-size: 1.5rem" class="py-10">
            {{ $t("noEventLogEntries") }}
          </v-col>

          <v-spacer />
        </v-row>
      </v-card>

      <DynamicList
        v-if="list.length > 0"
        :searchTerm="searchTerm"
        :pageSize="pageSize"
        :styles="styles"
        :data="list"
        :headers="headers"
        :columnSelection="columnSelection"
        class="px-8 py-2"
      />

      <v-row class="wrapper mt-5" cols="2" style="position: fixed" :style="{ top: position.top, left: position.left }">
        <v-card v-if="selectionIsOpen" :color="$vuetify.theme.dark ? '#1E1E1E' : 'grey lighten-2'" width="unset" class="mr-10 pa-5">
          <ColumnControl
            v-click-outside="handleOutsideClick"
            id="column-control"
            :headers="headers"
            :columnSelection="columnSelection"
            @update="updateSelectedColumns"
          />
        </v-card>
      </v-row>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "EventLogs",
  route: "EventLogs",
  components: {
    DynamicList: () => import("../DynamicList/DynamicList.vue"),
    ColumnControl: () => import("../DynamicList/ColumnControl.vue"),
    DatePicker: () => import("../../components/SimpleComponents/DatePicker.vue")
  },
  data: (vm) => ({
    list: [],
    styles: [
      {
        column: "details",
        selector: new RegExp(vm.$t("cancelled")),
        cssStyle: "text-decoration: line-through; color: #B71C1C"
      },
      {
        column: "U_severity",
        selector: /.*/,
        cssStyle: "text-transform: uppercase"
      },
      {
        column: "U_severity",
        selector: /^info$/i,
        className: "blue--text"
      },
      {
        column: "U_severity",
        selector: /^error$/i,
        className: "red--text"
      },
      {
        column: "U_severity",
        selector: /^warning$/i,
        className: "yellow--text"
      }
    ],
    headers: [],
    columnSelection: [],
    searchTerm: "",

    date: {
      from: vm.getDate(),
      until: vm.getDate()
    },

    selectionIsOpen: false,
    position: {
      top: undefined,
      left: undefined
    },
    lastRefresh: null,
    pageSizes: [5, 10, 25, 50, 100],
    pageSize: 10,

    loaderOverlay: false,
    refetchActive: false
  }),
  async created() {
    this.loaderOverlay = true;
    await this.fetchAndProcessDailyLogs();
    this.loaderOverlay = false;
  },
  watch: {
    searchTerm() {
      this.searchTerm ??= "";
    },
    pageSize(val) {
      this.pageSize = +val;
      this.pageSize ??= 0;
    }
  },
  methods: {
    async fetchDailyData() {
      return await this.$store.dispatch("eventLogs/getDailyLogs").catch(() => ({ data: [] }));
    },
    async fetchWithinRange(start, end) {
      return await this.$store.dispatch("eventLogs/getLogsInRange", { start, end }).catch(() => ({ data: [] }));
    },
    async fetchAndProcessDailyLogs() {
      const { data } = await this.fetchDailyData();

      this.processLogs(data);

      this.updateLastRefresh();
    },
    async fetchAndProcessLogs() {
      const start = this.date.from;
      const end = this.date.until;

      const { data } = await this.fetchWithinRange(start, end);

      this.processLogs(data);

      this.updateLastRefresh();
    },
    async processLogs(data) {
      this.list = data;
      this.list = this.makeDetailsString();
      this.list = this.sanitizeData();
      if (this.list.length === 0) return;

      this.list = this.translateTransactionType();

      this.headers = Object.keys(this.list[0]);
      this.list = this.orderByKeyArray(this.list, this.headers);

      this.columnSelection = structuredClone(this.headers);
    },
    dateToInt(date) {
      const rxNormalized = /(\d{2})(\.|-)(\d{2})(\.|-)(\d{4})/;

      return +date.replace(rxNormalized, "$5$3$1").replace(/\.|-/g, "");
    },
    async confirmRefetch() {
      return new Promise((resolve) => {
        this.$dialog
          .confirm(this.$t("dataRefreshConfirm"))
          .then(() => resolve(true))
          .catch(() => resolve(false));
      });
    },
    getDateDifference(start, end) {
      const date1 = new Date(start);
      const date2 = new Date(end);
      const msPerDay = 1000 * 60 * 60 * 24;

      const diffTime = date2.getTime() - date1.getTime();
      const diffDays = Math.ceil(diffTime / msPerDay);

      return diffDays;
    },
    async refetch() {
      const diff = this.getDateDifference(this.date.from, this.date.until);
      if (diff < 0) return this.$eventHub.$emit("showError", this.$t("dateRangeError"));

      if (diff > 7) {
        const confirmed = await this.confirmRefetch();

        if (!confirmed) return;
      }

      this.refetchActive = true;

      await this.fetchAndProcessLogs();

      this.$eventHub.$emit("showSuccess", this.$t("dataRefreshed"));

      this.refetchActive = false;
    },
    updateLastRefresh() {
      const date = this.getDate();
      const time = this.getTime();

      this.lastRefresh = `${date} - ${time}`;
    },
    handleOutsideClick() {
      this.selectionIsOpen = false;
    },
    translateTransactionType() {
      for (const entry of this.list) {
        entry.U_type = this.$t(entry.U_type);
      }

      return this.list;
    },
    translateWorkStatus(element) {
      return this.$t(element.U_status);
    },
    /* Reconstructing the objects in the correct order according to the headers */
    orderByKeyArray(data, keyOrder) {
      return data.map((obj) => {
        const former = structuredClone(obj);
        const newObj = {};

        for (const key of keyOrder) {
          newObj[key] = former[key];
        }

        return newObj;
      });
    },
    /* Ensure that all object have the same properties and add empty values to any newly added keys */
    sanitizeData() {
      const data = structuredClone(this.list);

      const keys = new Set();
      data.forEach((item) => {
        Object.keys(item).forEach((key) => keys.add(key));
      });

      data.forEach((item) => {
        keys.forEach((key) => {
          if (!(key in item) || item[key] === null) item[key] = "";
        });
      });

      for (let entry of data) {
        entry.U_date = entry.U_date.substr(0, 10);
      }

      return data;
    },
    makeDetailsString() {
      const data = structuredClone(this.list);
      const keysToDelete = [
        "Stage",
        "U_employeeId",
        "U_resourceId",
        "ProjectName",
        "BusinessPartner",
        "FinancialProject",
        "U_projectId",
        "U_project",
        "U_stageId",
        "U_status",
        "U_startDate",
        "U_endDate",
        "U_reason",

        "U_groupName",
        "U_groupRightGranted",
        "U_groupRightId",
        "U_passwordChanged",
        "U_authorId",
        "authorName",
        "U_text",

        "U_modelName",
        "U_validFrom"
      ];

      for (let entry of data) {
        switch (true) {
          case entry.U_type === "WorkStatus":
            const keys = Object.keys(entry);

            if (keys.includes("ProjectName")) entry.details = this.getProjectBookingDetails(entry);
            else if (keys.includes("U_status")) entry.details = this.getTimeKeepingDetails(entry);
            break;
          case entry.U_type === "AbsenceRequest":
            entry.details = this.getAbsenceRequestDetails(entry);
            break;
          case entry.U_type === "EditUserRights":
            entry.details = this.getUserEditDetails(entry);
            break;
          case entry.U_type === "TimeCorrection":
            entry.details = this.getTimeCorrectionDetails(entry);
            break;
          case entry.U_type === "TimeAccountAssignment":
            entry.details = this.getTimeAccountAssignmentDetails(entry);
            break;
          default:
            break;
        }

        if (!entry.details) entry.details = this.$t("cancelled");
        for (const key of keysToDelete) delete entry[key];
      }

      return data;
    },
    getTimeAccountAssignmentDetails(line) {
      const user = line.U_name;
      const author = line.authorName;
      const modelName = line.U_modelName;
      const validFrom = line.U_validFrom.substr(0, 10).split("-").reverse().join(".");

      return `${user} ${this.$t("wasAssignedNewTimeAccount")} ${this.$t("by")} ${author} - ${this.$t("activeFrom")} ${validFrom}: "${modelName}"`;
    },
    getProjectBookingDetails(project) {
      if (!project.ProjectName) return this.translateWorkStatus(project);
      if (!project.Stage) return `${this.translateWorkStatus(project)} - "${project.ProjectName}"`;

      return `${this.translateWorkStatus(project)} - "${project.ProjectName}, ${project.Stage.Description}, ${project.FinancialProject}"`;
    },
    getTimeCorrectionDetails(line) {
      const author = line.authorName;
      const userName = line.U_name;
      const text = line.U_text;

      return `${this.$t("timeCorrectionBy")} ${author} ${this.$t("for")} ${userName} - ${this.$t("comment")}: \"${text}\"`;
    },
    getTimeKeepingDetails(line) {
      return `${this.translateWorkStatus(line)}`;
    },
    getUserEditDetails(event) {
      if (event.U_passwordChanged === 1) return `${this.$t("passwordChangedBy")} ${event.authorName}`;

      const type = this.$t("userRight");
      const groupRightName = this.$t(event.U_groupName);
      const action = event.U_groupRightGranted === 1 ? this.$t("granted") : this.$t("revoked");
      const author = event.authorName;

      return `${type} "${groupRightName}" ${action} ${this.$t("by")} ${author}`;
    },
    getAbsenceRequestDetails({ U_reason, U_startDate, U_endDate }) {
      const absenceTimeFrame = U_startDate === U_endDate ? U_startDate : `${U_startDate} - ${U_endDate}`;
      return `${U_reason} (${absenceTimeFrame})`;
    },
    handleSelectionClick(event) {
      this.selectionIsOpen = !this.selectionIsOpen;

      const clickLeft = event.clientX;
      const clickTop = event.clientY;

      let card = document.getElementById("column-control");
      if (!card) card = { style: { width: 180 } };

      const cardWidth = card.style.width;
      const newLeft = clickLeft - cardWidth;

      this.position.top = `${clickTop}px`;
      this.position.left = `${newLeft}px`;
    },
    getDate() {
      const date = new Date();
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();

      return [day, month, year]
        .map((e) => e.toString().padStart(2, "0"))
        .reverse()
        .join("-");
    },
    getTime() {
      const date = new Date();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();

      return [hours, minutes, seconds].map((e) => e.toString().padStart(2, "0")).join(":");
    },
    getFullTimeStamp() {
      return `${this.getDate()}_${this.getTime()}`;
    },
    downloadCSVFile(csvString) {
      const fileName = `COBI-Time_Logs_${this.getFullTimeStamp()}.csv`;

      const blob = new Blob([csvString], { type: "text/csv" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },
    handleCSVExport() {
      const delimiter = ";";
      const headers = this.headers.map((header) => this.$t(header));
      const stringifiedElements = this.list.map((element) => Object.values(element).join(delimiter));

      const CSVOutput = `${headers.join(delimiter)}\n${stringifiedElements.join("\n")}`;

      this.downloadCSVFile(CSVOutput);
    },
    updateSelectedColumns(update) {
      this.columnSelection = structuredClone(update);
    }
  }
};
</script>

<style scoped>
.rotate {
  animation: rotation 2s infinite linear;
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(359deg);
  }
}
</style>
